import { motion } from "framer-motion";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import Seo, { breadcrumbSchema } from "@/components/Seo";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const VALUES_KEYS = [
  { title: "vc.about.v1-title", body: "vc.about.v1-body" },
  { title: "vc.about.v2-title", body: "vc.about.v2-body" },
  { title: "vc.about.v3-title", body: "vc.about.v3-body" },
  { title: "vc.about.v4-title", body: "vc.about.v4-body" },
];

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="about-page">
      <Seo
        title={t("vc.seo.about.title")}
        description={t("vc.seo.about.desc")}
        schema={breadcrumbSchema([{ name: "VangardClub", path: "/" }, { name: "About", path: "/about" }])}
      />
      <VangardHeader />

      <section className="relative overflow-hidden pt-[92px]">
        <VMark position="top-right" size={720} opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <motion.div {...fade} className="max-w-3xl">
            <div className="flex items-center gap-4 text-neutral-400">
              <span className="text-[11px] tracking-[0.35em] uppercase">{t("vc.about.eyebrow")}</span>
              <span className="h-px w-10 bg-white/15" />
              <span className="font-serif text-sm text-neutral-500">02</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-8 leading-[1.05]">
              {t("vc.about.title-1")} <span className="italic text-neutral-300">{t("vc.about.title-italic")}</span> {t("vc.about.title-2")}
            </h1>
            <p className="mt-8 text-lg text-neutral-400 font-light leading-relaxed">
              {t("vc.about.lede")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] overflow-hidden bg-[#080808]">
        <VMark position="bottom-left" size={620} opacity={0.04} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.about.story-eyebrow")}</div>
            <h2 className="font-serif text-4xl mt-6 leading-tight">
              {t("vc.about.story-title-1")} <span className="italic text-neutral-300">{t("vc.about.story-italic")}</span>
            </h2>
          </motion.div>
          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.1 }} className="lg:col-span-7 space-y-6 text-neutral-300 font-light leading-relaxed">
            <p>{t("vc.about.story-p1")}</p>
            <p>{t("vc.about.story-p2")}</p>
            <p>{t("vc.about.story-p3")}</p>
          </motion.div>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <motion.div {...fade}>
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.about.values-eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl mt-6">{t("vc.about.values-title")}</h2>
          </motion.div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06] rounded-3xl overflow-hidden">
            {VALUES_KEYS.map((v, i) => (
              <motion.div
                key={v.title}
                {...fade}
                transition={{ ...fade.transition, delay: 0.06 * i }}
                className="bg-[#0A0A0A] hover:bg-[#111] p-10 transition-colors"
              >
                <div className="font-serif text-5xl text-neutral-700">0{i + 1}</div>
                <h3 className="font-serif text-2xl mt-6">{t(v.title)}</h3>
                <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed max-w-[52ch]">{t(v.body)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <VangardFooter />
    </div>
  );
}
